import { memo, useCallback, useDeferredValue, useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { TooltipContentProps } from 'recharts'

import {
  Box,
  Flex,
  LightModeSemanticColors,
  TertiaryButton,
  Text,
  fontSizes,
} from '@enechain/ecloud-designsystem'
import { FaChevronRightRegular } from '@enechain/ecloud-icons'

import { GenerationTooltipContent } from '~/components/balance/GenerationTooltip'
import type { GenerationTooltipKoma } from '~/components/balance/GenerationTooltip'
import { GENERATION_LIST_SERIES_COLORS } from '~/constants/chartPalette'
import { iconSize } from '~/constants/iconSize'
import { useGenerationSourceList } from '~/hooks/useGenerationSourceList'
import { useJstMidnightTick } from '~/hooks/useJstMidnightTick'
import { formatChartTick } from '~/models/chartFormatting'
import { isTargetDateTodayInJst, toProtoDate } from '~/models/dateTime'
import { komaNumberToTime } from '~/models/komaToTime'
import { WORKFLOW_ID_PARAM } from '~/models/workflowIdParam'
import type {
  BgConfig,
  GenSeriesVisibility,
} from '~/pages/BalanceGeneration/constants'
import type {
  BgSummary,
  HatsuKomaData,
} from '~/pages/BalanceGeneration/models/hatsuKoma'
import { routePaths } from '~/routes/routePaths'

type Props = {
  bgConfig: BgConfig
  komaData: HatsuKomaData[]
  summary: BgSummary
  gcKoma: number
  /** 表示中の日付。当日以外は GC 縦線を描画しない (需要ダッシュボード
   *  `RegionChart` と同じ扱い。 spec doji-bg.md AC-BDB-022a と同趣旨)。 */
  displayDate: Date
  seriesVisibility: GenSeriesVisibility
  /** workflow-back-bar-readonly AC-14: 詳細リンクへ引き継ぐ caseId (undefined = 引き継がない)。 */
  caseId?: string
}

/**
 * 実績が1コマも無い日 (未来日等) の集計値プレースホルダ (#7695)。
 * 8080 (`js/hatsu-data.js:148` `_STAT_NA`) の「—」/ `Text.disabled` に対応する。
 * ラベル文字列 (最大/最小/利用率) は変えず、値だけこれに差し替える。
 */
const STAT_NA = (
  <Text as="span" color="Text.disabled">
    —
  </Text>
)
export const BgChart = memo(function BgChart({
  bgConfig: bg,
  komaData,
  summary,
  gcKoma,
  displayDate,
  seriesVisibility,
  caseId,
}: Props): React.ReactElement {
  const { t } = useTranslation()

  const gcTime = useMemo(() => komaNumberToTime(gcKoma + 1), [gcKoma])
  // GC 縦線は「当日の締め切り進行状態」を示すもので、 過去日 (全コマ確定済み) /
  // 未来日 (全コマ未確定) には意味を持たない。 判定は gcKoma の値から逆算せず
  // displayDate を JST 基準で直接見る — backend は未来日でも JST 23:00 以降は
  // 翌日コマをロック済みとして返すため、 gcKoma が 0 とは限らない (需要側
  // `RegionChart` / `BalanceDemandDetail/DetailChart` と同じ判定・同じ理由)。
  // 画面を開いたまま JST の日付境界を跨ぐと、 render 時計算の `isTodayDisplay` が
  // **前日の true のまま残り GC 縦線が居座る** (2026-09-07 レビュー指摘)。
  // 同時同量の 5 秒ポーリングには頼れない — React Query は structural sharing により
  // 応答が同一だと再 render を起こさないため。 発電BG詳細の `DetailChart` と
  // 需要側の `DateNav` が同じ理由で既にこの hook を使っている。
  useJstMidnightTick()
  const isTodayDisplay = isTargetDateTodayInJst(toProtoDate(displayDate))

  const planColor = GENERATION_LIST_SERIES_COLORS.genPlan
  const actualColor = GENERATION_LIST_SERIES_COLORS.genActual

  // 積み上げ順・色は 9 区分の SoT から導出する。 色の出どころは FilterCard の
  // switch と共通 (= 凡例として嘘にならないようにする)。
  //
  // [#7058 / AC-SPS-007・008] その SoT を **個人設定の永続値** に切り替えた
  // (`useGenerationSourceList`)。 doji-hatsu.md 機能詳細 1 が「積み上げ順序・色は
  // 個人設定画面でカスタマイズ可能」「凡例は個人設定で変更した順序・色を反映」と
  // 定めており、 それまでは既定値で固定されていた。 既定値は
  // `DEFAULT_STACK_ORDER_ITEMS` と同一なので未カスタマイズ時の見た目は変わらない。
  //
  // これにより #7064 が 5 区分時代に置いた**メリットオーダー** (再エネ→
  // ベースロード→火力 = 下から限界費用の安い順。 全 BG で同一順にすることで
  // 「どの BG が再エネ寄りか」 を横断比較できる。 design `js/charts.js:1478-1480`)
  // は、 個人設定の既定順 (原子力→火力3→地熱→太陽光→風力→水力→その他) に
  // 置き換わる。 積み上げ順の SoT を個人設定と定める doji-hatsu.md:175-176 に
  // 従った結果だが #7064 とは別原理なので、 #7810 (積み上げ順が spec 同士で
  // 未決着) の判断材料に含める。
  /* [#7058] 積み上げ順・色の変更は**遅延して**チャートへ反映する。
     モーダルで 🔼 / 🔽 を押すと個人設定のキャッシュが楽観更新され、 下の `key` により
     画面内の全 BG カードのチャートが作り直される。 これを同期で行うと、 押した行の
     並び替え (モーダルの一覧) の描画がチャート全数の再構築を待たされて反応が遅れる
     (実測: 8 枚 × 48 コマで 1 クリックあたり 0.4〜1.6 秒、 happy-dom)。
     `useDeferredValue` でチャート側を低優先度の描画に回し、 一覧を先に更新する。
     連打した場合も React は途中の値を飛ばして**最新の並びだけ**でチャートを作り直す。
     凡例トグル (`seriesVisibility`) はチャート自体を見て押す操作なので遅延させない。 */
  const latestSourceList = useGenerationSourceList()
  const sourceList = useDeferredValue(latestSourceList)
  const stackedBars = useMemo(() => {
    const visible = sourceList.filter((s) => seriesVisibility[s.key])
    return visible.map((s, i) => ({
      ...s,
      isTop: i === visible.length - 1,
    }))
  }, [seriesVisibility, sourceList])

  /* [#7058] recharts (3.8.1) は **mount 後に `<Bar>` の並びが変わっても積み順を
     組み替えない** (系列を初回 mount の登録順で保持する。 key を安定させたまま
     children を並べ替えると fill は各系列に追随するが位置は動かない)。
     個人設定は非同期に届くので、 チャートは既定順で mount → 設定到達 の順になり、
     **凡例だけ設定順・バーは既定順**という食い違いが残る (AC-SPS-007 の「即時反映」)。
     そこで積み順そのものを `key` にして、 順が変わった時だけチャートを作り直す。
     再構築が走るのは個人設定を変えた時だけで、 アニメーションは無効
     (`isAnimationActive={false}`) なので表示上の副作用は無い。 */
  const stackOrderKey = useMemo(
    () => stackedBars.map((b) => b.key).join('-'),
    [stackedBars],
  )

  const renderTooltipContent = useCallback(
    (props: TooltipContentProps) => (
      <GenerationTooltipContent
        {...(props as TooltipContentProps<number, string>)}
        t={t}
      />
    ),
    [t],
  )

  const axisTickStyle = {
    fontSize: 11,
    fill: LightModeSemanticColors.Text.midEmphasis.value,
  }

  return (
    <Flex
      flexDirection="column"
      overflow="hidden"
      minHeight={0}
      // カード本体は白 (`Surface.primary`)、 灰 (`Surface.secondary`) はヘッダのみ
      // (design `css/balance.css:230` `.region-card` / `:233` `.region-card-header`。
      // 発電カードも同じクラス系を使う)。 2026-09-07 以前は**逆**で、 需要ダッシュボード
      // (`RegionChart`) と並べた時に地色が食い違っていた (issue #7064)。
      background="Surface.primary"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="md"
    >
      {/* Header */}
      <Flex
        alignItems="center"
        flexShrink={0}
        gap="8px"
        /* ヘッダーの padding は design `css/balance.css:465`
           `.bg-card-header { padding:2px var(--space-2) 2px var(--space-3) }`
           = 上下 2px / 右 8px / 左 12px。 実装は `4px 8px` で上下が厚く左が浅かった
           (#7064)。 下の Stats 行 (:395) の `4px 8px` は design `.bg-card-stats` と
           一致しているので触らない。 */
        padding="2px 8px 2px 12px"
        background="Surface.secondary"
        borderBottomWidth="1px"
        borderBottomColor="Border.lowEmphasis"
      >
        {/* design `css/balance.css:234` `.region-card-header .region-title`
            = `--font-size-md` (14px) / 700。 2026-09-07 以前は sm (12px) で、
            需要ダッシュボードのエリア名と 2px 食い違っていた (issue #7064)。
            fontSize + fontWeight の組に一致する役割 typography が無いため raw 維持。 */}
        <span
          style={{
            fontSize: fontSizes.md.value,
            fontWeight: 700,
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {bg.name}
        </span>
        <Text
          as="span"
          color="Text.lowEmphasis"
          fontSize="xs"
          fontVariantNumeric="tabular-nums"
          title={t('page:balanceGeneration.chart.bgCodeLabel')}
        >
          {bg.code}
        </Text>
        {/* 調達区分 (自社発電 / 相対調達) は値だけ置くと何の分類か読めない
            (2026-09-14 デザイナー指摘「TH01 自社発電 が何を意味するか不明」)。
            枠付きチップにして「分類の値」であることを形で示し、名称は title で補う。

            [design 差異] design (`js/hatsu-data.js:411`) は素の span だが、
            design 側も同じく無ラベルで意味が読めない。DS `Badge status=` は
            **状態**の語彙 (positive/warning/...) なので調達区分のような
            カテゴリには使わない (`ui-feasibility/components/badge.md`)。

            寸法は DS トークン参照で組む (`ds-gap-defaults.md` 大原則「色・余白・font は
            必ず DS トークン参照。 生 hex / 生 px を書かない」)。 ヘッダの最も高い子は
            BG 名 span の 21px なので、 ヘッダ高 26px は変わらない。 */}
        <Text
          as="span"
          color="Text.midEmphasis"
          fontSize="xs"
          lineHeight="1.6"
          borderWidth="1px"
          borderColor="Border.lowEmphasis"
          borderRadius="sm"
          whiteSpace="nowrap"
          paddingX="1"
          title={t('page:balanceGeneration.chart.procurementTypeLabel')}
        >
          {bg.procurementType}
        </Text>
        {/*
          詳細導線は**文字リンク**。 アイコンだけ (chevron 単独) だと、 何が起きるかが
          hover しないと読めず、 同じ機能の需要ダッシュボード
          ([[RegionChart]]) が既に「詳細 + chevron」 の文字リンクなので、
          姉妹画面で導線の形が変わっていた (#7064)。 design
          `js/hatsu-data.js:411` も `<a class="ecl-text-link">詳細</a>`。

          **新しいタブで開くのは発電側だけ。** design は需要側 (`js/bg-data.js:283`) に
          `data-same-tab` を付けて同一タブ、 発電側 (`hatsu-data.js:411`) に
          `target="_blank" rel="noopener noreferrer"` を付けて別タブ、 と
          **意図的に書き分けている**。 発電ダッシュボードは BG カードを並べた
          監視ボード (gridstack で配置を作る面) なので、 詳細を見て戻るたびに
          ボードを組み直さずに済む。 title 属性も design の
          「新しいタブで BG 詳細を開く」 に合わせ、 挙動を事前に伝える。
        */}
        {/* 枠線付きの Tertiary ボタンにして導線の視認性を上げる
            (2026-09-14 デザイナー指摘)。

            [design 差異] design (`js/hatsu-data.js:411`) は
            `<a class="ecl-text-link">詳細</a>` の文字リンクだが、 カードを並べた
            監視ボードの中では文字リンクが情報に埋もれて見つけにくい。 枠を持たせて
            「押せる場所」 として立たせる。

            **高さは現行ヘッダに収まる 21px を明示する。** ヘッダの高さは
            `padding 2px + 最も高い子 + 2px + borderBottom 1px` で決まり、 従来は
            BG 名の span (21px) が最も高く **26px** だった。 22px にすると子の最大が
            入れ替わって 27px に伸びるため、 span と同じ 21px に合わせる (実測で
            26px 維持を確認)。 DS の `size="sm"` (32px) / `size="md"` (40px) は
            どちらもヘッダを押し広げるため使わない。 */}
        <TertiaryButton
          minWidth="auto"
          height="21px"
          minHeight="21px"
          marginLeft="8px"
          fontSize="sm"
          whiteSpace="nowrap"
          asChild
          paddingX="6px"
        >
          <RouterLink
            rel="noopener noreferrer"
            target="_blank"
            title={t('page:balanceGeneration.chart.detailLinkTitle')}
            to={`${routePaths.balanceGenerationDetail.path}?bg=${encodeURIComponent(bg.code)}${caseId !== undefined ? `&${WORKFLOW_ID_PARAM}=${caseId}` : ''}`}
          >
            {t('page:balanceGeneration.chart.detailLink')}
            <FaChevronRightRegular
              boxSize={iconSize.guideButton}
              aria-hidden="true"
            />
          </RouterLink>
        </TertiaryButton>
      </Flex>

      {/* Stats */}
      <Flex
        flexWrap="wrap"
        flexShrink={0}
        gap="10px"
        padding="4px 8px"
        color="Text.midEmphasis"
        fontSize="xs"
        borderBottomWidth="1px"
        borderBottomColor="Border.lowEmphasis"
      >
        <span>
          {t('page:balanceGeneration.card.max')}{' '}
          {summary.hasActual ? (
            <>
              {/* fontSize (sm) + fontWeight (700) の組が役割と一致しないため raw 維持。 */}
              <b
                style={{
                  fontSize: fontSizes.sm.value,
                  fontWeight: 700,
                  color: LightModeSemanticColors.Text.highEmphasis.value,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {summary.max.toLocaleString()}
              </b>
              {t('page:balanceGeneration.card.kwh')}{' '}
              <Text as="span" color="Text.lowEmphasis" fontSize="xs">
                {summary.maxTime}
              </Text>
            </>
          ) : (
            STAT_NA
          )}
        </span>
        <span>
          {t('page:balanceGeneration.card.min')}{' '}
          {summary.hasActual ? (
            <>
              {/* fontSize (sm) + fontWeight (700) の組が役割と一致しないため raw 維持。 */}
              <b
                style={{
                  fontSize: fontSizes.sm.value,
                  fontWeight: 700,
                  color: LightModeSemanticColors.Text.highEmphasis.value,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {summary.min.toLocaleString()}
              </b>
              {t('page:balanceGeneration.card.kwh')}{' '}
              <Text as="span" color="Text.lowEmphasis" fontSize="xs">
                {summary.minTime}
              </Text>
            </>
          ) : (
            STAT_NA
          )}
        </span>
        <span>
          {t('page:balanceGeneration.card.utilization')}{' '}
          {summary.hasActual ? (
            // fontSize (sm) + fontWeight (700) の組が役割と一致しないため raw 維持。
            <b
              style={{
                fontSize: fontSizes.sm.value,
                fontWeight: 700,
                color: LightModeSemanticColors.Text.highEmphasis.value,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {summary.avgUtilPct.toFixed(1)}%
            </b>
          ) : (
            STAT_NA
          )}
        </span>
      </Flex>

      {/* Axis labels */}
      {/* fontSize (xs) + fontWeight (600=semibold) の組が役割と一致しないため raw 維持。 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '1px 4px 0 0',
          fontSize: fontSizes.xs.value,
          fontWeight: 600,
          color: LightModeSemanticColors.Text.midEmphasis.value,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        <span>{t('page:balanceGeneration.card.kwh')}</span>
      </div>

      {/* Chart */}
      {/*
       * css で recharts の目盛テキストを 11px に固定する: Chakra preflight の
       * `"*": { font: "inherit" }` が全要素に効き、Recharts の `tick={{ fontSize }}`
       * (= SVG の presentation 属性 `font-size`) が body 継承値 (14px) に負けて効かない
       * (Supply48DataChart.tsx と同じ罠。値の出所は `axisTickStyle` のままにし、
       * 効かせ方だけ css 経由に変える)。この画面は単一 Y 軸 (左) のみで X 軸と
       * 同じ 11px のため selector は 1 本でよい。
       */}
      <Box
        flex={1}
        minHeight="160px"
        /* 左右 padding は入れない。 design `css/balance.css:479` の
           `.bg-chart-container { padding:0 0 2px }` + 同ファイルのコメント
           「左右 padding は除去 (同時同量3画面でプロット開始 x を揃える)」。
           2px 入れると需要 / 発電所別とプロット開始 x がずれる。
           bottom の 2px は x 軸ラベル用に残す。 */
        padding="0 0 2px"
        /* SVG の presentation 属性 (`font-size` / `font-weight`) は Chakra の
           preflight (`* { font: inherit }`) に負けるので、 **CSS 側で当てる**。
           `label={{ fontWeight: 700 }}` は属性にしかならず、 computed は
           `normal` のままだった (2026-09-07 レビュー指摘。 実測で確認)。
           太字は GC ラベル (`.recharts-label`) だけに効かせる — 軸目盛りも
           `.recharts-text` を持つので、 そちらへ広げると目盛りまで太る。 */
        css={{
          '& .recharts-text': { fontSize: '11px' },
          '& .recharts-label': { fontWeight: 700 },
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            key={stackOrderKey}
            margin={{ top: 12, right: 4, left: -15, bottom: 2 }}
            data={komaData satisfies GenerationTooltipKoma[]}
          >
            <CartesianGrid
              stroke={LightModeSemanticColors.Border.lowEmphasis.value}
            />
            <XAxis
              dataKey="time"
              interval={0}
              tick={axisTickStyle}
              tickFormatter={formatChartTick}
            />
            <YAxis
              width={52}
              tick={axisTickStyle}
              tickFormatter={(v: number) => Math.round(v).toLocaleString()}
            />
            <Tooltip
              cursor={{ fill: 'rgba(0,0,0,0.06)' }}
              content={renderTooltipContent}
              isAnimationActive={false}
            />

            {/* Stacked bars for source types */}
            {stackedBars.map(({ key, color, isTop }) => (
              <Bar
                key={key}
                fill={color}
                dataKey={key}
                isAnimationActive={false}
                radius={isTop ? [2, 2, 0, 0] : undefined}
                stackId="source"
              />
            ))}

            {/* Lines */}
            {seriesVisibility.genPlan && (
              <Line
                stroke={planColor}
                dataKey="genPlan"
                dot={false}
                isAnimationActive={false}
                strokeWidth={2}
                type="stepAfter"
              />
            )}
            {seriesVisibility.genActual && (
              <Line
                stroke={actualColor}
                dataKey="genActual"
                dot={false}
                isAnimationActive={false}
                strokeWidth={2}
                type="stepAfter"
              />
            )}

            {/* GC reference line。 非当日は描画しない (上の isTodayDisplay 参照)。 */}
            {isTodayDisplay && (
              <ReferenceLine
                /* GC の赤は **`Surface.negativeHigh`** (#d80004)。 `Text.negativeHigh` (#870103) は
                   暗いマルーンで、 design の GC 色 (`js/charts.js:876` `cssVar('surface-negative-high')`)
                   と別物 (2026-09-03 デザイナー指摘 #7064)。 線・ラベル・バッジで同じ 1 色を使う。 */
                stroke={LightModeSemanticColors.Surface.negativeHigh.value}
                /* 「GC HH:MM」 ラベル。 design `js/charts.js:1442-1443` に
                   「同時同量3画面で統一: 'GC HH:MM' / insideTopRight / 赤 bold。
                    発電のみラベル欠落していたため追加」 と経緯ごと残っている。
                   11px はコンテナの `.recharts-text` 規則にそのまま乗るので指定しない
                   (指定しても SVG 属性は CSS に負ける)。 */
                label={{
                  value: `${t('page:balanceGeneration.chart.gc')} ${gcTime}`,
                  position: 'insideTopRight',
                  fill: LightModeSemanticColors.Surface.negativeHigh.value,
                }}
                strokeDasharray="4 4"
                strokeWidth={2}
                x={gcTime}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Flex>
  )
})
